import { useCallback, useEffect, useState } from 'react';
import { useGet, UseGetParams, UseGetReturnType } from './useGet';

export interface UsePaginatedGetParams extends UseGetParams {
    limit: number;
}

export interface UsePaginatedGetReturnType<TData> extends Omit<UseGetReturnType<TData>, 'data'> {
    data: TData[];
    hasMore?: boolean;
    fetchMore: () => void;
    total?: number;
}

interface PaginatedResponse<TData> {
    data: TData[];
    hasMore: boolean;
    total?: number;
}

export const usePaginatedGet = <TData>({
    url,
    queryParams = {},
    limit
}: UsePaginatedGetParams): UsePaginatedGetReturnType<TData> => {
    const [data, setData] = useState<TData[]>([]);
    const [page, setPage] = useState(0);

    const allQueryParams = { ...queryParams, page: page.toString(), limit: limit.toString() };
    const { data: response, ...returnValues } = useGet<PaginatedResponse<TData>>({
        url,
        queryParams: allQueryParams
    });
    const { data: pageData, ...paginationDetails } = response ?? {};

    useEffect(() => {
        const isInitial = page === 0;
        const data = pageData ?? [];
        setData((prevData) => (isInitial ? data : [...prevData, ...data]));
    }, [pageData]);

    const queryParamsDependency = JSON.stringify(queryParams);

    useEffect(() => {
        setPage(0);
        setData([]);
    }, [queryParamsDependency]);

    const fetchMore = useCallback(() => {
        setPage((prevOffset) => prevOffset + 1);
    }, []);

    return { ...paginationDetails, ...returnValues, data, fetchMore };
};
